import { useState } from 'react';
import { formatEther, ethers } from 'ethers';
import axios from 'axios';

// Ankr RPC Configuration
const ANKR_API_KEY = '497e708c5f00391bc97397d6813d8e4a4d2da3b272024c4c62b96a1ec98a0a11';
const ANKR_RPC_URL = `https://rpc.ankr.com/eth/${ANKR_API_KEY}`;

export const useZwalletTokens = (ETHERSCAN_API_KEY, ETHERSCAN_BASE_URL) => {
  const [tokens, setTokens] = useState([]);
  const [newTokenAddress, setNewTokenAddress] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const fetchTokenBalances = async (account) => {
    if (!account) {
      console.warn('No account provided for token balance fetch');
      return;
    }

    setIsLoading(true);

    try {
      console.log('🪙 Fetching token balances for:', account);

      const url = `${ETHERSCAN_BASE_URL}?module=account&action=tokentx&address=${account}&page=1&offset=100&sort=desc&apikey=${ETHERSCAN_API_KEY}`;

      console.log('🔍 Etherscan token URL:', url.replace(ETHERSCAN_API_KEY, 'HIDDEN'));

      const response = await axios.get(url, { timeout: 10000 });
      console.log('🔍 Etherscan token API response received');

      if (response.data.status === '1' && response.data.result) {
        const tokenContracts = [...new Set(response.data.result.map(tx => tx.contractAddress))];

        console.log(`📊 Found ${tokenContracts.length} unique token contracts`);

        const tokenBalances = [];

        for (const contractAddress of tokenContracts.slice(0, 20)) {
          try {
            const tokenInfo = await fetchTokenInfo(contractAddress, account);
            if (tokenInfo && parseFloat(tokenInfo.balance) > 0) {
              tokenBalances.push(tokenInfo);
            }
          } catch (err) {
            console.warn(`Failed to fetch token ${contractAddress}:`, err.message);
          }
        }

        setTokens(tokenBalances);
        console.log(`✅ ${tokenBalances.length} token balances loaded`);
        return tokenBalances;
      } else if (response.data.status === '0') {
        const errorMessage = response.data.message || response.data.result || 'Unknown API error';

        if (errorMessage.includes('No transactions') ||
            errorMessage === 'NOTOK' ||
            errorMessage.includes('No records') ||
            errorMessage.includes('No token transfers') ||
            errorMessage.includes('No matching entries')) {
          console.log('ℹ️ No token transactions found or API error');
          setTokens([]);
          return [];
        } else if (errorMessage.includes('Invalid API Key')) {
          console.error('Invalid Etherscan API key');
          setTokens([]);
          throw new Error('Invalid API configuration. Please check your Etherscan API key.');
        } else {
          console.warn('Etherscan token API error:', errorMessage);
          setTokens([]);
          throw new Error(`Etherscan API: ${errorMessage}`);
        }
      } else {
        console.log('ℹ️ No token transactions found or API error');
        setTokens([]);
        return [];
      }
    } catch (err) {
      console.error('❌ Failed to fetch token balances:', err);
      setTokens([]);

      if (err.response?.status === 429) {
        throw new Error('API rate limit exceeded. Please try again in a moment.');
      } else if (err.code === 'NETWORK_ERROR' || err.message.includes('Network Error')) {
        throw new Error('Network error. Please check your internet connection.');
      } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        throw new Error('Request timeout. The network is slow, please try again.');
      } else {
        throw new Error('Failed to load token balances: ' + err.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchTokenInfo = async (contractAddress, account) => {
    try {
      const provider = new ethers.JsonRpcProvider(ANKR_RPC_URL);
      const contract = new ethers.Contract(contractAddress, [
        'function balanceOf(address) view returns (uint256)',
        'function decimals() view returns (uint8)',
        'function symbol() view returns (string)',
        'function name() view returns (string)'
      ], provider);

      const [balance, decimals, symbol, name] = await Promise.all([
        contract.balanceOf(account),
        contract.decimals(),
        contract.symbol(),
        contract.name()
      ]);

      const balanceValue = parseFloat(formatEther(balance)) / Math.pow(10, 18 - decimals);

      console.log(`💰 Token ${symbol}: ${balanceValue}`);

      return {
        address: contractAddress,
        name: name || 'Unknown Token',
        symbol: symbol || 'UNKNOWN',
        balance: balanceValue.toString(),
        decimals: decimals,
        balanceRaw: balance.toString()
      };
    } catch (err) {
      console.warn(`❌ Failed to fetch token info for ${contractAddress}:`, err.message);
      throw err;
    }
  };

  const addToken = async (tokenAddress, provider, account, existingTokens, ERC20_ABI, setError) => {
    if (!tokenAddress || !ethers.isAddress(tokenAddress)) {
      throw new Error('Please enter a valid token contract address');
    }

    const isDuplicate = existingTokens.some(token =>
      token.address.toLowerCase() === tokenAddress.toLowerCase()
    );

    if (isDuplicate) {
      throw new Error('This token is already in your list');
    }

    try {
      setIsLoading(true);
      setError('');

      const tokenInfo = await fetchTokenInfo(tokenAddress, account);

      if (tokenInfo && parseFloat(tokenInfo.balance) >= 0) {
        const updatedTokens = [...existingTokens, tokenInfo];
        setTokens(updatedTokens);

        console.log('✅ Token added successfully:', tokenInfo.symbol);
        return { success: true, token: tokenInfo };
      } else {
        throw new Error('Failed to fetch token information');
      }
    } catch (err) {
      console.error('❌ Failed to add token:', err);
      throw new Error('Failed to add token: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const removeToken = (tokenAddress) => {
    const updatedTokens = tokens.filter(token => token.address !== tokenAddress);
    setTokens(updatedTokens);
    console.log('🗑️ Token removed:', tokenAddress);
  };

  const refreshTokenBalance = async (tokenAddress, account) => {
    try {
      const tokenInfo = await fetchTokenInfo(tokenAddress, account);
      if (tokenInfo) {
        setTokens(prev =>
          prev.map(token =>
            token.address === tokenAddress ? tokenInfo : token
          )
        );
        console.log('✅ Token balance refreshed:', tokenInfo.symbol);
        return tokenInfo;
      }
    } catch (err) {
      console.warn('Failed to refresh token balance:', err);
    }
    return null;
  };

  const refreshAllTokenBalances = async (account) => {
    console.log('🔄 Refreshing all token balances...');
    await fetchTokenBalances(account);
  };

  const getTokenByAddress = (address) => {
    return tokens.find(token => token.address.toLowerCase() === address.toLowerCase());
  };

  const getTokensWithBalance = () => {
    return tokens.filter(token => parseFloat(token.balance) > 0);
  };

  const calculateTotalTokenValue = () => {
    return tokens.reduce((total, token) => {
      const price = ['USDC', 'USDT', 'DAI'].includes(token.symbol) ? 1 : 0;
      return total + (parseFloat(token.balance) * price);
    }, 0);
  };

  const clearTokens = () => {
    setTokens([]);
    console.log('🗑️ All tokens cleared');
  };

  const importTokens = async (tokenList, provider, account) => {
    console.log('📥 Importing tokens from list:', tokenList.length);

    const importedTokens = [];

    for (const tokenAddress of tokenList.slice(0, 10)) {
      try {
        const tokenInfo = await fetchTokenInfo(tokenAddress, account);
        if (tokenInfo) {
          importedTokens.push(tokenInfo);
        }
      } catch (err) {
        console.warn(`Failed to import token ${tokenAddress}:`, err.message);
      }
    }

    setTokens(prev => [...prev, ...importedTokens]);
    console.log(`✅ ${importedTokens.length} tokens imported`);
    return importedTokens;
  };

  const exportTokens = () => {
    const exportData = {
      tokens: tokens,
      timestamp: new Date().toISOString(),
      count: tokens.length
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });

    return URL.createObjectURL(dataBlob);
  };

  return {
    tokens,
    newTokenAddress,
    setNewTokenAddress,
    fetchTokenBalances,
    addToken,
    removeToken,
    refreshTokenBalance,
    refreshAllTokenBalances,
    getTokenByAddress,
    getTokensWithBalance,
    calculateTotalTokenValue,
    clearTokens,
    importTokens,
    exportTokens,
    isLoading
  };
};

export default useZwalletTokens;
